import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ALL_CAPTURE_MODES,
  PRINT_FRAME_MODES,
  PRINT_FRAME_TARGET,
  type CaptureMode,
  type EffectIdName,
  type PrintFrameMode,
  type ViewfinderFrameAsset,
  type PrintFrameAsset,
  isValidSlug,
} from '@photobooth/shared';

import styles from '../styles/app.module.css';
import { createBooth, uploadBlob } from '../api/client';
import { EFFECTS } from '../gpu/registry';
import { Button } from '../ui/Button';
import { FooterCredit } from '../ui/FooterCredit';
import { Toast } from '../ui/Toast';
import { MagicWandPicker } from '../builder/MagicWandPicker';
import { CropPicker } from '../builder/CropPicker';
import { loadImageBitmap } from '../builder/magicWand';
import { logger } from '../utils/log';

const log = logger('builder');

const MODE_LABELS: Record<CaptureMode, string> = {
  single: 'Single photo',
  strip3: '3-photo strip',
  strip4: '4-photo strip',
  video: '30s video',
};

const PRINT_FRAME_LABELS: Record<PrintFrameMode, string> = {
  single: 'Single photo',
  strip3: '3-photo strip',
  strip4: '4-photo strip',
};

/**
 * Title → slug. Lowercased, spaces and non-alnum collapsed to single
 * hyphens, edges trimmed, capped to 40 chars. Output may be empty when the
 * title is itself empty or made of disallowed characters.
 */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics (escaped)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

interface FrameDraft {
  /** Local URL for preview while building (object URL, revoked on submit). */
  previewUrl: string;
  blob: Blob;
  width: number;
  height: number;
  label?: string;
}

export function BuilderPage() {
  const navigate = useNavigate();

  // Identity
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  /**
   * Tracks whether the user has manually edited the slug field. Until they
   * do, typing in Title auto-fills a slugified version. Once they edit slug
   * directly (or paste), we stop auto-syncing so we don't overwrite their
   * choice.
   */
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [email, setEmail] = useState('');

  const handleTitleChange = (next: string) => {
    setTitle(next);
    if (!slugManuallyEdited) {
      setSlug(slugify(next));
    }
  };

  const handleSlugChange = (next: string) => {
    // Normalize anything pasted/typed to the slug shape but don't claim "user
    // edited" until the value differs from what auto-sync would produce.
    const normalized = slugify(next);
    setSlug(normalized);
    if (normalized !== slugify(title)) {
      setSlugManuallyEdited(true);
    } else if (normalized === '') {
      // Clearing the field returns control to auto-sync.
      setSlugManuallyEdited(false);
    }
  };

  // Features
  const [modes, setModes] = useState<Set<CaptureMode>>(
    () => new Set<CaptureMode>(['single', 'strip3', 'strip4', 'video']),
  );
  const [effects, setEffects] = useState<Set<EffectIdName>>(
    () => new Set<EffectIdName>(EFFECTS.map((e) => e.id)),
  );

  // Assets
  const [viewfinderFrames, setViewfinderFrames] = useState<FrameDraft[]>([]);
  const [printFrames, setPrintFrames] = useState<
    Partial<Record<PrintFrameMode, FrameDraft>>
  >({});

  // Crop + magic-wand modal state. The two modals chain: a print-frame
  // upload first goes through CropPicker, then optionally through the
  // magic wand.
  const [cropTarget, setCropTarget] = useState<
    | {
        file: File;
        mode: PrintFrameMode;
        targetWidth: number;
        targetHeight: number;
      }
    | null
  >(null);
  const [wandTarget, setWandTarget] = useState<
    | {
        // Either an original file (viewfinder), or a cropped blob (print).
        file: File;
        kind: 'viewfinder' | 'print';
        printMode?: PrintFrameMode;
        label?: string;
      }
    | null
  >(null);

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const slugError = useMemo(() => {
    if (slug.length === 0) return null;
    if (!isValidSlug(slug)) {
      return 'Use 3–40 lowercase letters, digits, or hyphens. Can\'t be a reserved word.';
    }
    return null;
  }, [slug]);

  const emailError = useMemo(() => {
    if (email.length === 0) return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      ? null
      : 'Enter a valid email address.';
  }, [email]);

  const canSubmit =
    !busy &&
    title.trim().length > 0 &&
    isValidSlug(slug) &&
    !emailError &&
    email.length > 0 &&
    modes.size > 0 &&
    effects.size > 0;

  const toggleMode = (m: CaptureMode) =>
    setModes((s) => {
      const n = new Set(s);
      if (n.has(m)) n.delete(m);
      else n.add(m);
      return n;
    });

  const toggleEffect = (e: EffectIdName) =>
    setEffects((s) => {
      const n = new Set(s);
      if (n.has(e)) n.delete(e);
      else n.add(e);
      return n;
    });

  const handleViewfinderPicked = useCallback(
    (file: File, withWand: boolean, label?: string) => {
      if (!file.type.startsWith('image/')) {
        setToast('Please pick an image file.');
        return;
      }
      if (withWand) {
        setWandTarget({ file, kind: 'viewfinder', ...(label ? { label } : {}) });
      } else {
        (async () => {
          const bm = await loadImageBitmap(file);
          const draft: FrameDraft = {
            previewUrl: URL.createObjectURL(file),
            blob: file,
            width: bm.width,
            height: bm.height,
            ...(label ? { label } : {}),
          };
          bm.close();
          setViewfinderFrames((prev) => [...prev, draft]);
        })();
      }
    },
    [],
  );

  const handlePrintFramePicked = useCallback(
    (file: File, mode: PrintFrameMode) => {
      if (!file.type.startsWith('image/')) {
        setToast('Please pick an image file.');
        return;
      }
      const target = PRINT_FRAME_TARGET[mode];
      setCropTarget({
        file,
        mode,
        targetWidth: target.width,
        targetHeight: target.height,
      });
    },
    [],
  );

  // After crop confirmation, ask the user whether to magic-wand the result.
  const handleCropConfirm = useCallback(
    (result: { blob: Blob; width: number; height: number }) => {
      if (!cropTarget) return;
      const mode = cropTarget.mode;
      // Wrap blob into a File so MagicWandPicker can ingest it.
      const cropped = new File([result.blob], `print-${mode}.png`, {
        type: 'image/png',
      });
      setCropTarget(null);
      // Auto-promote to wand step (user can press "Keep as-is" if they
      // don't want background removal).
      setWandTarget({ file: cropped, kind: 'print', printMode: mode });
    },
    [cropTarget],
  );

  const handleWandConfirm = useCallback(
    (result: { blob: Blob; width: number; height: number }) => {
      if (!wandTarget) return;
      const previewUrl = URL.createObjectURL(result.blob);
      const draft: FrameDraft = {
        previewUrl,
        blob: result.blob,
        width: result.width,
        height: result.height,
        ...(wandTarget.label ? { label: wandTarget.label } : {}),
      };
      if (wandTarget.kind === 'viewfinder') {
        setViewfinderFrames((prev) => [...prev, draft]);
      } else if (wandTarget.printMode) {
        setPrintFrames((prev) => {
          const next = { ...prev };
          const old = next[wandTarget.printMode!];
          if (old) URL.revokeObjectURL(old.previewUrl);
          next[wandTarget.printMode!] = draft;
          return next;
        });
      }
      setWandTarget(null);
    },
    [wandTarget],
  );

  const removeViewfinderFrame = (idx: number) => {
    setViewfinderFrames((prev) => {
      const f = prev[idx];
      if (f) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const removePrintFrame = (mode: PrintFrameMode) => {
    setPrintFrames((prev) => {
      const next = { ...prev };
      const old = next[mode];
      if (old) URL.revokeObjectURL(old.previewUrl);
      delete next[mode];
      return next;
    });
  };

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      log.info('submit', { slug, title, modes: [...modes], effects: [...effects] });
      const printFrameRequests: Partial<
        Record<PrintFrameMode, { width: number; height: number; contentType: 'image/png' }>
      > = {};
      for (const m of PRINT_FRAME_MODES) {
        const pf = printFrames[m];
        if (pf) {
          printFrameRequests[m] = {
            width: pf.width,
            height: pf.height,
            contentType: 'image/png',
          };
        }
      }
      const res = await createBooth({
        slug,
        title: title.trim(),
        email,
        modes: [...modes],
        effects: [...effects],
        assets: {
          viewfinderFrames: viewfinderFrames.map((f) => ({
            ...(f.label ? { label: f.label } : {}),
            width: f.width,
            height: f.height,
            contentType: 'image/png',
          })),
          printFrames: printFrameRequests,
        },
      });

      // Upload each asset to its presigned URL.
      const uploads: Promise<unknown>[] = [];
      for (let i = 0; i < viewfinderFrames.length; i++) {
        const f = viewfinderFrames[i]!;
        const u = res.uploads.viewfinderFrames[i]!;
        uploads.push(uploadBlob(u.uploadUrl, f.blob, 'image/png'));
      }
      for (const m of PRINT_FRAME_MODES) {
        const pf = printFrames[m];
        const u = res.uploads.printFrames[m];
        if (pf && u) {
          uploads.push(uploadBlob(u.uploadUrl, pf.blob, 'image/png'));
        }
      }
      await Promise.all(uploads);
      log.info('all assets uploaded');

      // Revoke local URLs.
      for (const f of viewfinderFrames) URL.revokeObjectURL(f.previewUrl);
      for (const pf of Object.values(printFrames)) URL.revokeObjectURL(pf.previewUrl);

      navigate(`/${res.slug}`, { replace: true, state: { justCreated: true } });
    } catch (err) {
      log.error('create failed', err);
      setToast(err instanceof Error ? err.message : 'Failed to create booth');
    } finally {
      setBusy(false);
    }
  }, [
    canSubmit,
    slug,
    title,
    email,
    modes,
    effects,
    viewfinderFrames,
    printFrames,
    navigate,
  ]);

  return (
    <div
      style={{
        minHeight: '100vh',
        maxWidth: 720,
        margin: '0 auto',
        padding: '24px 16px 80px',
        color: 'var(--text)',
      }}
    >
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Make your own photobooth</h1>
        <p style={{ color: 'var(--text-dim)', margin: '6px 0 0' }}>
          Pick a vanity URL, choose effects, drop in some frame art. Stays live for 30 days; we&apos;ll email you a renewal link.
        </p>
      </header>

      <Section title="Identity">
        <Field label="Title" hint="Shown on your booth.">
          <input
            className={styles.builderInput}
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            maxLength={80}
            placeholder="Ray's birthday booth"
          />
        </Field>
        <Field
          label="Vanity URL"
          hint={
            slugManuallyEdited
              ? '3–40 lowercase letters / digits / hyphens.'
              : 'Auto-filled from the title — edit to customize.'
          }
          error={slugError}
        >
          <div className={styles.urlField}>
            <span>photobooth.ofunc.io/</span>
            <input
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              maxLength={40}
              placeholder="ray-bday-2026"
            />
          </div>
        </Field>
        <Field label="Email" hint="For renewal notices. Never shown publicly." error={emailError}>
          <input
            className={styles.builderInput}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            maxLength={254}
          />
        </Field>
      </Section>

      <Section title="Capture modes">
        <div style={chipGroupStyle}>
          {ALL_CAPTURE_MODES.map((m) => (
            <Toggle
              key={m}
              label={MODE_LABELS[m]}
              checked={modes.has(m)}
              onChange={() => toggleMode(m)}
            />
          ))}
        </div>
      </Section>

      <Section title="Effects">
        {(['filter', 'distortion', 'overlay'] as const).map((cat) => (
          <div key={cat} style={{ marginBottom: 16 }}>
            <div
              style={{
                textTransform: 'uppercase',
                fontSize: 11,
                color: 'var(--text-dim)',
                letterSpacing: 1.2,
                marginBottom: 6,
              }}
            >
              {cat}
            </div>
            <div style={chipGroupStyle}>
              {EFFECTS.filter((e) => e.category === cat).map((e) => (
                <Toggle
                  key={e.id}
                  label={e.label}
                  checked={effects.has(e.id)}
                  onChange={() => toggleEffect(e.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Viewfinder frames"
        hint="Transparent PNGs composited over the live preview. Upload one per device size — mobile, tablet, desktop. The booth picks the closest aspect ratio at runtime."
      >
        <FrameSlot
          label="Add a frame"
          onPick={(file, withWand, label) => handleViewfinderPicked(file, withWand, label)}
          allowLabel
        />
        {viewfinderFrames.map((f, i) => (
          <FramePreview
            key={i}
            url={f.previewUrl}
            label={`${f.label ?? `Frame ${i + 1}`} — ${f.width}×${f.height}`}
            onRemove={() => removeViewfinderFrame(i)}
          />
        ))}
      </Section>

      <Section
        title="Print-out frames (optional)"
        hint="Template art for each output layout. Upload an image, crop it to the right shape, then optionally remove the background where photos should show through."
      >
        {PRINT_FRAME_MODES.map((m) => {
          if (!modes.has(m)) return null; // hide modes the user disabled
          const draft = printFrames[m];
          const target = PRINT_FRAME_TARGET[m];
          return (
            <PrintFrameSlot
              key={m}
              mode={m}
              targetWidth={target.width}
              targetHeight={target.height}
              draft={draft}
              onPick={(file) => handlePrintFramePicked(file, m)}
              onRemove={() => removePrintFrame(m)}
            />
          );
        })}
      </Section>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
        <Button variant="ghost" onClick={() => navigate('/')}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {busy ? 'Creating…' : 'Create booth'}
        </Button>
      </div>

      {cropTarget && (
        <CropPicker
          file={cropTarget.file}
          targetWidth={cropTarget.targetWidth}
          targetHeight={cropTarget.targetHeight}
          title={`Crop for ${PRINT_FRAME_LABELS[cropTarget.mode]}`}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropTarget(null)}
        />
      )}
      {wandTarget && (
        <MagicWandPicker
          file={wandTarget.file}
          onConfirm={handleWandConfirm}
          onCancel={() => setWandTarget(null)}
        />
      )}
      {toast && <Toast message={toast} />}
      <FooterCredit />
    </div>
  );
}

// -----------------------------------------------------------------------
// Local presentational helpers — kept inline because they're builder-only.
// -----------------------------------------------------------------------

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
      }}
    >
      <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>{title}</h2>
      {hint && (
        <p style={{ margin: '0 0 12px', color: 'var(--text-dim)', fontSize: 13 }}>{hint}</p>
      )}
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>{label}</span>
      {children}
      {hint && !error && (
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>
          {hint}
        </span>
      )}
      {error && (
        <span style={{ display: 'block', fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
          {error}
        </span>
      )}
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(): void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      style={{
        padding: '8px 14px',
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,0.18)',
        background: checked ? '#ff3a8c' : 'rgba(0,0,0,0.4)',
        color: checked ? '#fff' : 'var(--text)',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {label}
    </button>
  );
}

const chipGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
};

function FrameSlot({
  label,
  onPick,
  allowLabel,
}: {
  label: string;
  /** withWand=true sends the file through MagicWandPicker before saving. */
  onPick(file: File, withWand: boolean, label?: string): void;
  allowLabel?: boolean;
}) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [frameLabel, setFrameLabel] = useState('');
  const [stripBg, setStripBg] = useState(false);
  return (
    <div
      style={{
        border: '1px dashed rgba(255,255,255,0.18)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 12,
      }}
    >
      {!pendingFile && (
        <label style={{ display: 'block', cursor: 'pointer', color: 'var(--text-dim)' }}>
          <input
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              if (f) setPendingFile(f);
            }}
          />
          <span style={{ fontSize: 13 }}>{label} — click to upload a PNG/JPG</span>
        </label>
      )}
      {pendingFile && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 13 }}>
            Picked: <strong>{pendingFile.name}</strong>
          </div>
          {allowLabel && (
            <input
              className={styles.builderInput}
              value={frameLabel}
              onChange={(e) => setFrameLabel(e.target.value)}
              placeholder="Label (e.g. Mobile, Desktop)"
            />
          )}
          <label style={{ fontSize: 13, color: 'var(--text-dim)', display: 'inline-flex', gap: 8 }}>
            <input
              type="checkbox"
              checked={stripBg}
              onChange={(e) => setStripBg(e.target.checked)}
            />
            Use magic-wand to remove background (best for solid white/green)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="ghost"
              onClick={() => {
                setPendingFile(null);
                setFrameLabel('');
                setStripBg(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onPick(pendingFile, stripBg, frameLabel || undefined);
                setPendingFile(null);
                setFrameLabel('');
                setStripBg(false);
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FramePreview({
  url,
  label,
  onRemove,
}: {
  url: string;
  label: string;
  onRemove(): void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '64px 1fr auto',
        gap: 12,
        alignItems: 'center',
        padding: 8,
        borderRadius: 8,
        background: 'rgba(0,0,0,0.3)',
        marginBottom: 8,
      }}
    >
      <img
        src={url}
        alt=""
        style={{
          width: 64,
          height: 64,
          objectFit: 'contain',
          background:
            'conic-gradient(#333 25%, transparent 0 50%, #333 0 75%, transparent 0) 0 0 / 12px 12px',
          borderRadius: 6,
        }}
      />
      <div style={{ fontSize: 13 }}>{label}</div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        style={{
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.18)',
          color: 'var(--text-dim)',
          borderRadius: 999,
          width: 28,
          height: 28,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  );
}

function PrintFrameSlot({
  mode,
  targetWidth,
  targetHeight,
  draft,
  onPick,
  onRemove,
}: {
  mode: PrintFrameMode;
  targetWidth: number;
  targetHeight: number;
  draft?: FrameDraft;
  onPick(file: File): void;
  onRemove(): void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '72px 1fr auto',
        gap: 12,
        alignItems: 'center',
        padding: 10,
        marginBottom: 8,
        background: 'rgba(0,0,0,0.3)',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 8,
          background:
            draft
              ? `url(${draft.previewUrl}) center/contain no-repeat, conic-gradient(#333 25%, transparent 0 50%, #333 0 75%, transparent 0) 0 0 / 12px 12px`
              : 'rgba(255,255,255,0.05)',
          border: '1px dashed rgba(255,255,255,0.15)',
        }}
        aria-hidden
      />
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {PRINT_FRAME_LABELS[mode]}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          Target: {targetWidth} × {targetHeight}
          {draft && ` — ready`}
        </div>
      </div>
      {draft ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove"
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.18)',
            color: 'var(--text-dim)',
            borderRadius: 999,
            width: 28,
            height: 28,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      ) : (
        <label
          style={{
            padding: '8px 14px',
            background: '#ff3a8c',
            color: '#fff',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Upload
          <input
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPick(f);
              // reset so the same file can be picked again
              e.currentTarget.value = '';
            }}
          />
        </label>
      )}
    </div>
  );
}

export type { ViewfinderFrameAsset, PrintFrameAsset };
